import {Card, Spinner} from 'react-bootstrap'
import {useQuery} from '@apollo/client'
import {Link, useParams} from 'react-router-dom'
import {defineMessages, useIntl} from 'react-intl'
import {GET_AGENT_ONBOARDING_APPLICATION} from '../../gql/queries/agentOnboardingQueries'
import {IAgentOnboardingApplication} from '../../types/agentOnboarding'
import {useGetUser} from '../../store/userStore'
import {hasPermission} from '../../helpers'
import {ALL_PERMISSIONS} from '../../helpers/consts'
import {CarrierAppointments} from './components/CarrierAppointments'
import {ApplicationDocuments} from './components/ApplicationDocuments'
import {UplineSection} from './components/UplineSection'
import {ApplicationInfo} from './components/ApplicationInfo'
import {InternalNotes} from './components/InternalNotes'
import {CommissionProposal} from './components/CommissionProposal'

const M = defineMessages({
  back: {id: 'onboarding.detail.back', defaultMessage: 'Back to onboarding'},
  notFound: {id: 'onboarding.detail.notFound', defaultMessage: 'Application not found.'},
  fieldUpline: {id: 'onboarding.detail.field.upline', defaultMessage: 'Upline'},
  noUpline: {id: 'onboarding.detail.noUpline', defaultMessage: 'No upline assigned'},
})
const dash = '—'

export const OnboardingDetailPage = () => {
  const intl = useIntl()
  const {id} = useParams<{id: string}>()
  const permissions = useGetUser((s) => s.user?.permissions) ?? []
  const canEdit = hasPermission(permissions, ALL_PERMISSIONS.CHANGE_AGENT_ONBOARDING)

  const {data, loading, refetch} = useQuery<{
    agentOnboardingApplication: IAgentOnboardingApplication | null
  }>(GET_AGENT_ONBOARDING_APPLICATION, {variables: {id}, fetchPolicy: 'cache-and-network'})

  const app = data?.agentOnboardingApplication

  if (loading && !data) {
    return (
      <Card>
        <Card.Body className='d-flex justify-content-center' style={{height: '40vh'}}>
          <Spinner animation='border' />
        </Card.Body>
      </Card>
    )
  }

  if (!app) {
    return (
      <Card>
        <Card.Body className='text-center text-gray-500 py-10'>
          {intl.formatMessage(M.notFound)}
        </Card.Body>
      </Card>
    )
  }

  return (
    <>
      <div className='mb-6'>
        <Link to='/onboarding' className='btn btn-sm btn-light mb-4'>
          <i className='bi bi-arrow-left me-1' />
          {intl.formatMessage(M.back)}
        </Link>
        <h1 className='fw-bold text-gray-900 mb-1'>{app.fullName || dash}</h1>
        <span className='text-gray-600'>{app.email || dash}</span>
        {/* Fuera de la lista de siete filas iguales: de quién cuelga el agente es una decisión
            de negocio, y que falte tiene que verse sin abrir nada. */}
        <div className='d-flex align-items-center gap-2 mt-3'>
          <i className='bi bi-diagram-3 fs-6 text-gray-500' />
          <span className='text-gray-500 fs-7'>{intl.formatMessage(M.fieldUpline)}</span>
          {app.uplineName ? (
            <span className='badge badge-light-primary fw-bold fs-7'>{app.uplineName}</span>
          ) : (
            <span className='badge badge-light-warning fw-bold fs-7'>
              {intl.formatMessage(M.noUpline)}
            </span>
          )}
        </div>
      </div>

      <ApplicationInfo
        app={app}
        canEdit={canEdit}
        onSaved={() => {
          refetch()
        }}
      />

      <ApplicationDocuments
        applicationId={app.id}
        documents={app.documents}
        canEdit={canEdit}
        onChanged={() => {
          refetch()
        }}
      />

      <div className='row g-5 mb-5'>
        <div className='col-xl-6'>
          <UplineSection
            applicationId={app.id}
            currentUplineName={app.uplineName}
            canEdit={canEdit}
            onSaved={() => {
              refetch()
            }}
          />
        </div>
        <div className='col-xl-6'>
          <InternalNotes
            applicationId={app.id}
            notes={app.internalNotes}
            canEdit={canEdit}
          />
        </div>
      </div>

      {/* Ancho completo: era la única tarjeta de su fila y dejaba media pantalla vacía. */}
      <div className='mb-5'>
        <CommissionProposal
          applicationId={app.id}
          savedLevel={app.commissionLevel}
          sentAt={app.commissionProposalSentAt}
          canEdit={canEdit}
          onSaved={() => {
            refetch()
          }}
        />
      </div>

      <CarrierAppointments
        applicationId={app.id}
        appointments={app.carrierAppointments}
        canEdit={canEdit}
        onSaved={() => {
          refetch()
        }}
      />
    </>
  )
}
